import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { useAuth } from "hooks";
import { useLazyListCollectionQuery } from "store/services/collectionApi";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";
import { exportExcelFile } from "utils/exportExcelFile";
import { toast } from "react-toastify";
import moment from "moment";

const EXCEL_DATA_INITIAL_STATE = { dateRange: [], shipmentId: null };

const ExportCollectionExcel = ({ isBusy = false }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const { viewEntity } = useAuth();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });

  const [fetchShipmentFulfillment] = useLazyListCollectionQuery();
  const [fetchShipment, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLShipmentListQuery();

  // Function to trigger shipment DDL API when dialog is opened
  const handleOpenDialog = () => {
    setShowDialog(true);
    fetchShipment({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  };

  const handleDownload = async (data) => {
    const { dateRange } = data;

    const fromDate = moment(dateRange[0]).format("MM-DD-YYYY");
    const toDate = moment(dateRange[1]).format("MM-DD-YYYY");

    const payload = {
      shipmentId: data?.shipmentId?._id,
      fromDate,
      toDate,
    };

    try {
      const responseData = await fetchShipmentFulfillment(payload).unwrap();
      if (!responseData.data || responseData.data.length === 0) {
        toast.error("No Data Found!");
        return;
      }
      const formattedData = responseData.data.map((row, index) => ({
        SN: index + 1,
        "Collection ID": row.collectionId || "N/A",
        "Shipment ID":
          Array.isArray(row.shipmentrequest) && row.shipmentrequest.length
            ? row.shipmentrequest.map((s) => s.shipmentId || s._id).join(", ")
            : "N/A",
        "Vehicle ID": row.vehicle?.vehicle_id || "N/A",
        "Driver ID": row.driver?.driverId || "N/A",
        "Pickup Time": row.actualPickupTime || "N/A",
        Status: row.status ? "Completed" : "Cancelled",
      }));

      exportExcelFile({
        sheetData: formattedData,
        fileName: `Shipment_Fulfillment_${
          data?.shipmentId?.shipmentId || "All"
        }_${new Date().toISOString().slice(0, 10)}.xlsx`,
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export data!");
    } finally {
      handleReset();
    }
  };

  const handleReset = () => {
    setShowDialog(false);
    setShowForm(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Download Excel" arrow>
        <IconButton color="success" onClick={handleOpenDialog}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Collections"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={showForm}
              onChange={(e) => setShowForm(e.target.checked)}
              icon={<CheckBoxOutlineBlankOutlined />}
              checkedIcon={<CheckBoxOutlined />}
            />
          }
          label="Filter by specific Shipments"
        />

        {showForm && (
          <AppForm loading={isBusy}>
            <AppForm.Content>
              <Controller
                name="shipmentId"
                control={control}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Shipments"
                    options={shipments || []}
                    getOptionLabel={(option) => option.shipmentId}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    loading={loadingShipments}
                    loadingText="Loading shipments..."
                    onChange={(_, value) => field.onChange(value)}
                    value={field.value}
                  />
                )}
              />
            </AppForm.Content>
          </AppForm>
        )}

        <Controller
          name="dateRange"
          control={control}
          rules={{ required: "Date range is required" }}
          render={({ field, fieldState: { error } }) => (
            <DateRangeToggle
              {...field}
              label="Select Date Range"
              error={!!error}
              helperText={error?.message}
              required
            />
          )}
        />
      </AppConfirmDialog>
    </>
  );
};

export default ExportCollectionExcel;